import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../store/authStore';
import { useTranslation } from 'react-i18next';
import { signInWithGoogle, renderGoogleButton } from '../../services/googleAuth';

interface RegisterForm {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  first_name: string;
  last_name: string;
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { register: registerUser, loginWithGoogle } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>();

  const password = watch('password');

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    try {
      const idToken = await signInWithGoogle();
      await loginWithGoogle(idToken);
      toast.success(t('auth.accountCreated'));
      navigate(redirectTo || '/');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Google sign-up failed';
      toast.error(errorMessage);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  useEffect(() => {
    // Render Google button if available
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (googleButtonRef.current && googleClientId) {
      // Clear any existing content
      googleButtonRef.current.innerHTML = '';
      
      // Small delay to ensure Google script is fully loaded
      const timer = setTimeout(() => {
        renderGoogleButton(googleButtonRef.current!, handleGoogleSignUp, {
          theme: 'outline',
          size: 'large',
          text: 'signup_with',
        }).catch((err) => {
          console.error('Failed to render Google button:', err);
          toast.error('Failed to load Google sign-in. Please refresh the page.');
        });
      }, 100);

      return () => clearTimeout(timer);
    } else if (!googleClientId) {
      console.warn('VITE_GOOGLE_CLIENT_ID is not set');
    }
  }, []);

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    try {
      await registerUser({
        email: data.email,
        username: data.username,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name || undefined,
      });
      toast.success(t('auth.accountCreated'));
      // Redirect to the original destination (e.g., /join/CODE) or home
      navigate(redirectTo || '/');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || t('auth.registrationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('register.title')}</h2>
        <p className="text-surface-400">
          {t('register.subtitle')}
        </p>
      </div>

      {/* Google Sign Up - Primary */}
      {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
        <div className="mb-6">
          <div className="flex justify-center">
            <div ref={googleButtonRef} id="google-signup-button" className="min-h-[40px]"></div>
          </div>
        </div>
      )}

      {/* Regular Registration - Secondary */}
      {import.meta.env.VITE_GOOGLE_CLIENT_ID && (
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface-900 text-surface-400">
                {t('auth.orSignUpWithEmail', 'Or sign up with email')}
              </span>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="input-group">
            <label className="input-label">{t('register.firstName')}</label>
            <input
              type="text"
              className="input"
              placeholder={t('register.firstName')}
              {...register('first_name', { required: t('register.required') })}
            />
            {errors.first_name && (
              <p className="text-xs text-red-400">{errors.first_name.message}</p>
            )}
          </div>
          <div className="input-group">
            <label className="input-label">{t('register.lastName')}</label>
            <input
              type="text"
              className="input"
              placeholder={t('register.lastName')}
              {...register('last_name')}
            />
          </div>
        </div>

        <div className="input-group">
          <label className="input-label">{t('register.email')}</label>
          <input
            type="email"
            className="input"
            placeholder={t('register.email')}
            {...register('email', {
              required: t('register.emailRequired'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('register.emailInvalid'),
              },
            })}
          />
          {errors.email && (
            <p className="text-sm text-red-400">{errors.email.message}</p>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">{t('register.username')}</label>
          <input
            type="text"
            className="input"
            placeholder={t('register.username')}
            {...register('username', {
              required: t('register.usernameRequired'),
              minLength: {
                value: 3,
                message: t('register.usernameMin'),
              },
            })}
          />
          {errors.username && (
            <p className="text-sm text-red-400">{errors.username.message}</p>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">{t('register.password')}</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className="input pr-12"
              placeholder="••••••••"
              {...register('password', {
                required: t('register.passwordRequired'),
                minLength: {
                  value: 8,
                  message: t('register.passwordMin'),
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-white transition-colors"
            >
              {showPassword ? (
                <EyeSlashIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-400">{errors.password.message}</p>
          )}
        </div>

        <div className="input-group">
          <label className="input-label">{t('register.confirmPassword')}</label>
          <input
            type="password"
            className="input"
            placeholder="••••••••"
            {...register('confirmPassword', {
              required: t('register.confirmRequired'),
              validate: (value) =>
                value === password || t('register.confirmMismatch'),
            })}
          />
          {errors.confirmPassword && (
            <p className="text-sm text-red-400">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <motion.button
          type="submit"
          disabled={isLoading || isGoogleLoading}
          className="btn-primary w-full mt-6"
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>{t('register.creating')}</span>
            </div>
          ) : (
            t('register.create')
          )}
        </motion.button>
      </form>

      <div className="mt-6 text-center">
        <p className="text-surface-400">
          {t('register.haveAccount')}{' '}
          <Link
            to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'}
            className="text-primary-400 hover:text-primary-300 font-medium"
          >
            {t('register.signIn')}
          </Link>
        </p>
      </div>
    </div>
  );
}

